import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, RefreshCw, Trash2, MousePointer2, Eye, ShoppingCart, TrendingUp, ChevronDown, ChevronUp, Home, FileText, BadgeDollarSign, Info } from 'lucide-react';
import { useAnalytics, DateRange } from '../hooks/useAnalytics';
import { MetricsCard } from '../components/MetricsCard';
export function DashboardPage() {
  const {
    pageMetrics,
    dateRange,
    setDateRange,
    refreshData,
    resetData,
    lastRefreshed
  } = useAnalytics();
  const [expandedSection, setExpandedSection] = useState<string | null>('landing');
  if (!pageMetrics) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-1633">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ginger" data-id="element-1634"></div>
      </div>;
  }
  // Group pages by type
  const landingPages = pageMetrics.filter(p => p.pageName.includes('Landing') || p.pageName.includes('Options'));
  const advertorialPages = pageMetrics.filter(p => p.pageName.includes('Advertorial'));
  const salesPages = pageMetrics.filter(p => p.pageName.includes('Sales:'));
  const checkoutPages = pageMetrics.filter(p => p.pageName.includes('Checkout'));
  // Calculate totals
  const totalViews = pageMetrics.reduce((acc, m) => acc + m.views, 0);
  const totalClicks = pageMetrics.reduce((acc, m) => acc + m.clicks, 0);
  const totalConversions = pageMetrics.reduce((acc, m) => acc + m.conversions, 0);
  const avgCTR = totalViews > 0 ? totalClicks / totalViews * 100 : 0;
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const renderPageGroup = (title: string, pages: typeof pageMetrics, sectionKey: string, emptyMessage: string, icon: React.ReactNode) => {
    const isExpanded = expandedSection === sectionKey;
    const groupViews = pages.reduce((acc, p) => acc + p.views, 0);
    const groupClicks = pages.reduce((acc, p) => acc + p.clicks, 0);
    const groupCTR = groupViews > 0 ? groupClicks / groupViews * 100 : 0;
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-1635">
        {/* Section Header - Clickable */}
        <button onClick={() => toggleSection(sectionKey)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1636">
          <div className="flex items-center gap-4" data-id="element-1637">
            <div className="flex items-center gap-2" data-id="element-1638">
              <div className="text-ginger" data-id="element-1639">{icon}</div>
              <h3 className="text-lg font-bold text-charcoal" data-id="element-1640">{title}</h3>
            </div>
            {groupViews > 0 && <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-1641">
                <span className="flex items-center gap-1" data-id="element-1642">
                  <Eye className="w-4 h-4" data-id="element-1643" />
                  {groupViews.toLocaleString()} views
                </span>
                <span className="flex items-center gap-1" data-id="element-1644">
                  <MousePointer2 className="w-4 h-4" data-id="element-1645" />
                  {groupClicks.toLocaleString()} clicks
                </span>
                <span className="text-ginger font-medium" data-id="element-1646">
                  {groupCTR.toFixed(1)}% CTR
                </span>
              </div>}
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1647" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1648" />}
        </button>

        {/* Section Content */}
        {isExpanded && <div className="border-t border-gray-200" data-id="element-1649">
            {pages.length === 0 ? <div className="px-6 py-8 text-center text-gray-500" data-id="element-1650">
                {emptyMessage}
              </div> : <div className="overflow-x-auto" data-id="element-1651">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1652">
                  <thead className="bg-gray-50" data-id="element-1653">
                    <tr data-id="element-1654">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1655">
                        Page
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1656">
                        Views
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1657">
                        Clicks
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1658">
                        CTR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1659">
                        Conversions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1660">
                    {pages.map(page => <tr key={page.path} className="hover:bg-gray-50 transition-colors" data-id="element-1661">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1662">
                          <div data-id="element-1663">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1664">
                              {page.pageName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1665">
                              {page.path}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-1666">
                          {page.views.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-1667">
                          {page.clicks.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1668">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${page.ctr > 20 ? 'bg-green-100 text-green-800' : page.ctr > 10 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1669">
                            {page.ctr.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-1670">
                          {page.conversions.toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}
      </motion.div>;
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-charcoal" data-id="element-1671">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-1672">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-1673">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1674">
            <div className="flex items-center gap-3" data-id="element-1675">
              <div className="bg-ginger/10 p-2 rounded-lg" data-id="element-1676">
                <BarChart3 className="w-6 h-6 text-ginger" data-id="element-1677" />
              </div>
              <div data-id="element-1678">
                <h1 className="text-xl font-bold text-charcoal tracking-tight" data-id="element-1679">
                  Page Analytics Dashboard
                </h1>
                <p className="text-xs text-gray-500" data-id="element-1680">
                  Last updated: {lastRefreshed.toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3" data-id="element-1681">
              {/* Date Range Selector */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1" data-id="element-1682">
                {(['today', 'last7days', 'last30days', 'all_time'] as DateRange[]).map(range => <button key={range} onClick={() => setDateRange(range)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${dateRange === range ? 'bg-white text-charcoal shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1683">
                    {range === 'today' ? 'Today' : range === 'last7days' ? '7 Days' : range === 'last30days' ? '30 Days' : 'All Time'}
                  </button>)}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2" data-id="element-1684">
                <button onClick={refreshData} className="p-2 text-gray-500 hover:text-ginger hover:bg-ginger/5 rounded-lg transition-colors" title="Refresh Data" data-id="element-1685">
                  <RefreshCw className="w-5 h-5" data-id="element-1686" />
                </button>
                <button onClick={resetData} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Reset All Data" data-id="element-1687">
                  <Trash2 className="w-5 h-5" data-id="element-1688" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1689">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-1690">
          <MetricsCard title="Total Page Views" value={totalViews.toLocaleString()} icon={<Eye className="w-5 h-5" data-id="element-1692" />} color="charcoal" delay={0} data-id="element-1691" />
          <MetricsCard title="Total Clicks (CTA)" value={totalClicks.toLocaleString()} subtitle={`${avgCTR.toFixed(1)}% CTR`} icon={<MousePointer2 className="w-5 h-5" data-id="element-1694" />} color="blue" delay={0.1} data-id="element-1693" />
          <MetricsCard title="Total Conversions" value={totalConversions.toLocaleString()} icon={<ShoppingCart className="w-5 h-5" data-id="element-1696" />} color="green" delay={0.2} data-id="element-1695" />
          <MetricsCard title="Overall Conversion Rate" value={`${(totalClicks > 0 ? totalConversions / totalClicks * 100 : 0).toFixed(1)}%`} subtitle="From Click to Purchase" icon={<TrendingUp className="w-5 h-5" data-id="element-1698" />} color="ginger" delay={0.3} data-id="element-1697" />
        </div>

        {/* Page Groups */}
        {renderPageGroup('Landing Pages', landingPages, 'landing', 'No landing page traffic yet. Send visitors to your home or options page.', <Home className="w-5 h-5" data-id="element-1699" />)}

        {renderPageGroup('Advertorial Pages', advertorialPages, 'advertorial', 'No advertorial traffic yet. These are your educational content pages.', <FileText className="w-5 h-5" data-id="element-1700" />)}

        {renderPageGroup('Sales Pages', salesPages, 'sales', 'No sales page traffic yet. These are your product-specific pages.', <BadgeDollarSign className="w-5 h-5" data-id="element-1701" />)}

        {renderPageGroup('Checkout', checkoutPages, 'checkout', 'No checkout traffic yet.', <ShoppingCart className="w-5 h-5" data-id="element-1702" />)}

        {/* Help Text */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8" data-id="element-1703">
          <div className="flex items-center gap-2 mb-2" data-id="element-1704">
            <Info className="w-4 h-4 text-blue-900" data-id="element-1705" />
            <h4 className="text-sm font-semibold text-blue-900" data-id="element-1706">
              How to Read This Dashboard
            </h4>
          </div>
          <ul className="text-sm text-blue-800 space-y-1" data-id="element-1707">
            <li data-id="element-1708">
              <strong data-id="element-1709">Views:</strong> Number of times the page was loaded
            </li>
            <li data-id="element-1710">
              <strong data-id="element-1711">Clicks:</strong> Number of CTA button clicks on that page
            </li>
            <li data-id="element-1712">
              <strong data-id="element-1713">CTR (Click-Through Rate):</strong> Percentage of visitors
              who clicked a CTA (Clicks ÷ Views × 100)
            </li>
            <li data-id="element-1714">
              <strong data-id="element-1715">Conversions:</strong> Number of purchases/checkouts from
              that page
            </li>
          </ul>
        </motion.div>
      </main>
    </div>;
}